import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import {
  createCamera,
  deleteCamera,
  fetchCameraList,
  fetchDetailCamera,
  updateCamera,
} from '@/domains/asset-management/apis/camera-management';
import type {
  CameraDetailResponse,
  CameraID,
  CameraListResponse,
  CreateCameraPayload,
  CreateCameraResponse,
  DeleteCameraResponse,
  UpdateCameraParams,
  UpdateCameraPayload,
  UpdateCameraResponse,
  UseCameraManagementProps,
} from '@/domains/asset-management/types/camera';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';

const useCameraManagement = ({
  cctvId = null,
  queryParams = '',
  onDeleteSuccess = () => {},
  onCreateSuccess = () => {},
  onUpdateSuccess = () => {},
  refetchInterval,
}: UseCameraManagementProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: cameraListData, isLoading: isLoadingCameraList } = useQuery<CameraListResponse>({
    queryKey: ['cameraListData', queryParams],
    queryFn: () => fetchCameraList(queryParams),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 1,
    refetchInterval: refetchInterval,
    refetchIntervalInBackground: true,
    initialData: {
      code: '-',
      message: '-',
      data: { total: 0, page: 1, size: 10, total_pages: 0, rows: [] },
    },
    initialDataUpdatedAt: 0,
  });

  const { data: detailCameraData, refetch: refetchDetailCamera } = useQuery<
    CameraDetailResponse,
    Error
  >({
    queryKey: ['detailCameraData', cctvId],
    queryFn: () => fetchDetailCamera(cctvId as CameraID),
    retry: false,
    enabled: !!cctvId,
  });

  const createCameraMutation = useMutation<CreateCameraResponse, Error, CreateCameraPayload>({
    mutationFn: (cameraData) => createCamera(cameraData),
    onSuccess: (responseData) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > registration success'), {
        callback() {
          queryClient.invalidateQueries({ queryKey: ['cameraListData', queryParams] });
          onCreateSuccess(responseData.data);
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(message));
    },
  });

  const deleteCameraMutation = useMutation<DeleteCameraResponse, Error, CameraID>({
    mutationFn: (cctvId) => deleteCamera(cctvId),
    onSuccess: () => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > delete success'), {
        callback() {
          onDeleteSuccess();
          queryClient.invalidateQueries({ queryKey: ['cameraListData', queryParams] });
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(message));
    },
  });

  const updateCameraMutation = useMutation<
    UpdateCameraResponse,
    Error,
    { cameraData: UpdateCameraPayload; params: UpdateCameraParams }
  >({
    mutationFn: ({ cameraData, params }) => updateCamera(cameraData, params),
    onSuccess: (responseData) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > update success'), {
        callback() {
          queryClient.invalidateQueries({ queryKey: ['cameraListData', queryParams] });
          onUpdateSuccess(responseData.data);
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(message));
    },
  });

  return {
    cameraListData,
    isLoadingCameraList,
    detailCameraData,
    refetchDetailCamera,
    createCamera: createCameraMutation.mutate,
    updateCamera: updateCameraMutation.mutate,
    deleteCamera: deleteCameraMutation.mutate,
  };
};

export default useCameraManagement;
